import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '@eombor/ui';
import { t } from '../i18n/index.js';
import { useFieldAppearance } from './field-appearance.js';
import { useAnchoredPlacement, type AnchoredPlacement } from './anchored-placement.js';

/**
 * QIDIRUVLI TANLAGICH.
 *
 * Nega `<select>` emas: bu yerdagi ro'yxatlar yuzlab satrdan iborat
 * (tuzilmada 500 dan ortiq birlik bor) va odam ularni aylantirib emas,
 * terib topadi.
 *
 * Nega ro'yxat PORTALDA: joyida chizilganda u forma balandligini
 * o'zgartirib, ostidagi maydonlarni pastga surib yuborardi. Portal +
 * `position: fixed` bilan ro'yxat hech narsani surmaydi va oyna tanasining
 * `overflow-y-auto` chegarasida kesilib ham qolmaydi. Joyi `resize` va
 * `scroll` da (capture bilan — ichki aylantirgichlar ham hisobga olinsin)
 * qayta hisoblanadi.
 */

const MAX_LIST_HEIGHT = 288;

export interface ComboboxOption {
  value: string;
  label: string;
  /** O'ng chekkadagi ikkilamchi belgi — kod yoki qisqa nom. */
  hint?: string | null;
  /** Turni bildiruvchi nishon. */
  badge?: ReactNode;
}

export interface ComboboxProps {
  label?: ReactNode;
  placeholder?: string;
  /** Hech narsa topilmaganda ko'rsatiladigan matn. */
  emptyText: string;
  hint?: string;
  error?: string;
  options: ComboboxOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  /**
   * Berilsa qidiruv TASHQARIDA bajariladi (server so'rovi) va ichki
   * filtrlash o'chadi — aks holda serverdan kelgan javob ikkinchi marta,
   * bu safar noto'g'ri mezon bilan qisqartirilardi.
   */
  onSearch?: (query: string) => void;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
}

export const Combobox = ({
  label,
  placeholder,
  emptyText,
  hint,
  error,
  options,
  value,
  onChange,
  onSearch,
  loading = false,
  disabled = false,
  className,
  id,
}: ComboboxProps) => {
  const autoId = useId();
  const inputId = id ?? autoId;
  const refined = useFieldAppearance() === 'refined';
  const listId = `${inputId}-list`;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;

  const anchorRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [placement, setPlacement] = useState<AnchoredPlacement | null>(null);

  const selected = useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value],
  );

  const visible = useMemo(() => {
    if (onSearch !== undefined || query === '') return options;
    const needle = query.toLowerCase();
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(needle) ||
        (option.hint ?? '').toLowerCase().includes(needle),
    );
  }, [options, query, onSearch]);

  /* Ro'yxatning o'zi panel: chegara (2) va oynada `p-1.5` (12) uning
     balandligiga kiradi, shuning uchun joy hisobida alohida olinadi. */
  const chrome = refined ? 14 : 2;
  useAnchoredPlacement({
    open,
    anchorRef,
    panelRef: listRef,
    options: { maxListHeight: MAX_LIST_HEIGHT - chrome, chrome },
    onPlace: setPlacement,
    onDetach: () => {
      setOpen(false);
      setQuery('');
    },
  });

  // Tashqariga bosilganda yopiladi. `mousedown` — `click` emas: ro'yxatdagi
  // satr `click` ga qadar yo'q bo'lib qolsa, tanlov umuman ishlamasdi.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (anchorRef.current?.contains(target) === true) return;
      if (listRef.current?.contains(target) === true) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const close = (): void => {
    setOpen(false);
    setQuery('');
  };

  const pick = (option: ComboboxOption): void => {
    onChange(option.value);
    close();
    inputRef.current?.blur();
  };

  const openList = (): void => {
    if (disabled) return;
    setActive(0);
    setOpen(true);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        openList();
        return;
      }
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((current) => {
        if (visible.length === 0) return 0;
        return (current + step + visible.length) % visible.length;
      });
      return;
    }

    if (event.key === 'Enter' && open) {
      const option = visible[active];
      if (option !== undefined) {
        event.preventDefault();
        pick(option);
      }
      return;
    }

    if (event.key === 'Escape' && open) {
      // Oyna ichida ishlaydi: `Esc` ro'yxatni yopadi, oynani EMAS.
      event.stopPropagation();
      close();
    }
  };

  const describedBy = cn(error ? errorId : undefined, !error && hint ? hintId : undefined);

  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      {label === undefined ? null : (
        <label htmlFor={inputId} className="text-text text-sm font-medium">
          {label}
        </label>
      )}

      <div ref={anchorRef} className="relative flex items-center">
        <input
          ref={inputRef}
          id={inputId}
          role="combobox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={open && visible[active] ? `${listId}-${active}` : undefined}
          aria-invalid={error === undefined ? undefined : true}
          aria-describedby={describedBy.length > 0 ? describedBy : undefined}
          autoComplete="off"
          disabled={disabled}
          placeholder={placeholder}
          value={open ? query : (selected?.label ?? '')}
          onFocus={openList}
          onClick={openList}
          onKeyDown={onKeyDown}
          onChange={(event) => {
            const next = event.target.value;
            setQuery(next);
            setActive(0);
            setOpen(true);
            onSearch?.(next.trim());
          }}
          className={refined ? cn(
            'field-refined text-text h-[var(--size-control)] w-full min-w-0',
            'border px-3 pr-16 text-base',
          ) : cn(
            'h-[var(--size-control)] w-full min-w-0 rounded-[var(--radius-sm)]',
            'bg-surface text-text border px-3 pr-16 text-base',
            'transition-[border-color] duration-[var(--motion-fast)] ease-[var(--ease-standard)]',
            'disabled:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-60',
            error === undefined
              ? 'border-border-control hover:border-border-strong'
              : 'border-danger',
          )}
        />

        <div className="absolute right-2 flex items-center gap-0.5">
          {selected === null || disabled ? null : (
            <button
              type="button"
              aria-label={t('form.clearSelection')}
              onClick={() => {
                onChange(null);
                close();
              }}
              className="text-text-faint hover:text-text flex size-6 items-center justify-center rounded-[var(--radius-sm)]"
            >
              <X size={14} aria-hidden />
            </button>
          )}
          <ChevronDown
            size={16}
            aria-hidden
            className={cn(
              'text-text-faint pointer-events-none',
              refined ? 'transition-transform duration-[180ms] ease-[var(--ease-standard)]' : undefined,
              refined && open ? 'rotate-180' : undefined,
              refined && disabled ? 'opacity-50' : undefined,
            )}
          />
        </div>
      </div>

      {error === undefined ? (
        hint === undefined ? null : (
          <p id={hintId} className="text-text-faint text-xs">
            {hint}
          </p>
        )
      ) : (
        <p id={errorId} className="text-danger text-xs">
          {error}
        </p>
      )}

      {open && placement !== null
        ? createPortal(
            <ul
              ref={listRef}
              id={listId}
              role="listbox"
              style={{
                position: 'fixed',
                left: placement.left,
                width: placement.width,
                top: placement.top,
                bottom: placement.bottom,
                maxHeight: placement.listMaxHeight + chrome,
              }}
              className={
                refined
                  ? cn(
                      'motion-listbox-in z-[var(--z-popover)] overflow-y-auto overscroll-contain p-1.5',
                      'border-border bg-surface rounded-[var(--radius-md)] border',
                      'shadow-[var(--shadow-3)]',
                    )
                  : cn(
                      'z-[var(--z-popover)] overflow-y-auto overscroll-contain',
                      'border-border bg-surface rounded-[var(--radius-sm)] border',
                      'shadow-[var(--shadow-3)]',
                    )
              }
            >
              {loading ? (
                <li className="text-text-faint px-3 py-2.5 text-sm">…</li>
              ) : visible.length === 0 ? (
                <li className="text-text-faint px-3 py-2.5 text-sm">{emptyText}</li>
              ) : (
                visible.map((option, index) => (
                  <li key={option.value}>
                    <button
                      type="button"
                      id={`${listId}-${index}`}
                      // `role="option"` tugmaning O'ZIDA: `li` ga qo'yilsa,
                      // bosish nishoni bilan e'lon qilingan variant ikki
                      // xil element bo'lib qolardi.
                      role="option"
                      aria-selected={option.value === value}
                      // `blur` dan oldin ishlashi uchun `mousedown`: `click`
                      // kelguncha input fokusni yo'qotib, ro'yxat yopilardi.
                      onMouseDown={(event) => {
                        event.preventDefault();
                        pick(option);
                      }}
                      onMouseEnter={() => setActive(index)}
                      className={
                        refined
                          ? cn(
                              'flex min-h-11 w-full items-center gap-3 rounded-[var(--radius-sm)] px-3 py-2 text-left',
                              'transition-colors duration-[var(--motion-fast)]',
                              option.value === value ? 'bg-accent-soft' : undefined,
                              index === active && option.value !== value ? 'bg-surface-2' : undefined,
                            )
                          : cn(
                              'flex w-full items-center gap-2 px-3 py-2 text-left',
                              index === active ? 'bg-surface-2' : undefined,
                            )
                      }
                    >
                      <span
                        className={cn(
                          'min-w-0 flex-1 text-sm',
                          refined ? 'line-clamp-2' : 'truncate',
                          refined && option.value === value ? 'text-accent font-medium' : 'text-text',
                        )}
                      >
                        {option.label}
                      </span>
                      {option.hint === null || option.hint === undefined ? null : (
                        <span
                          className={cn(
                            'tabular shrink-0',
                            refined
                              ? cn(
                                  'rounded-[0.375rem] px-1.5 py-0.5 text-[0.6875rem]',
                                  option.value === value
                                    ? 'bg-surface/70 text-accent'
                                    : 'bg-surface-2 text-text-muted',
                                )
                              : 'text-text-faint text-xs',
                          )}
                        >
                          {option.hint}
                        </span>
                      )}
                      {option.badge === undefined ? null : (
                        <span className="shrink-0">{option.badge}</span>
                      )}
                      {refined && option.value === value ? (
                        <Check size={16} aria-hidden className="text-accent shrink-0" />
                      ) : null}
                    </button>
                  </li>
                ))
              )}
            </ul>,
            document.body,
          )
        : null}
    </div>
  );
};
